import React, { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Input } from '@/shared/ui'
import { useLegalEntities, useRegions } from '@/shared/hooks'
import { AdminClientTariffService } from '@/shared/api'
import type { ClientTariffPreviewRequest, ClientTariffPreviewResponse } from '@/shared/api'
import { KIND_LABELS } from './ClientTariffFormModal'

interface ClientTariffPreviewModalProps {
  isOpen: boolean
  onClose: () => void
}

export const ClientTariffPreviewModal: React.FC<ClientTariffPreviewModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { data: companies } = useLegalEntities({ page: 1, limit: 100, status: 1 })
  const { data: regions } = useRegions(true)

  const [legalEntityId, setLegalEntityId] = useState<number | ''>('')
  const [regionId, setRegionId] = useState<number | ''>('')
  const [deliveryType, setDeliveryType] = useState('')
  const [interval, setIntervalValue] = useState('')
  const [origin, setOrigin] = useState('')

  const [result, setResult] = useState<ClientTariffPreviewResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (isOpen) return
    setLegalEntityId('')
    setRegionId('')
    setDeliveryType('')
    setIntervalValue('')
    setOrigin('')
    setResult(null)
    setError(null)
    setIsLoading(false)
  }, [isOpen])

  if (!isOpen) return null

  const handleCalculate = async () => {
    if (legalEntityId === '') {
      setError('Выберите компанию')
      return
    }
    if (regionId === '') {
      setError('Выберите город')
      return
    }

    const body: ClientTariffPreviewRequest = {
      legalEntityId: Number(legalEntityId),
      regionId: Number(regionId),
      ...(deliveryType.trim() ? { deliveryType: deliveryType.trim() } : {}),
      ...(interval.trim() ? { interval: interval.trim() } : {}),
      ...(origin.trim() ? { origin: origin.trim() } : {}),
    }

    setError(null)
    setResult(null)
    setIsLoading(true)
    try {
      setResult(await AdminClientTariffService.preview(body))
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Ошибка расчёта')
    } finally {
      setIsLoading(false)
    }
  }

  const previewData = result?.result ? result.data : null
  const resultError = result && !result.result ? result.error ?? 'Нет действующего тарифа' : null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Предпросмотр расчёта</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={isLoading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Компания</label>
              <select
                className="admin-input"
                value={legalEntityId}
                onChange={(e) =>
                  setLegalEntityId(e.target.value ? Number(e.target.value) : '')
                }
              >
                <option value="">— выберите компанию —</option>
                {(companies?.items ?? []).map((company) => (
                  <option key={company.legalEntityId} value={company.legalEntityId}>
                    {company.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">Город</label>
              <select
                className="admin-input"
                value={regionId}
                onChange={(e) => setRegionId(e.target.value ? Number(e.target.value) : '')}
              >
                <option value="">— выберите город —</option>
                {(regions ?? []).map((region) => (
                  <option key={region.id} value={region.id}>
                    {region.name}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Тип доставки"
              value={deliveryType}
              placeholder="Например, курьер"
              onChange={(e) => setDeliveryType(e.target.value)}
            />

            <Input
              label="Интервал"
              value={interval}
              placeholder="Например, 2"
              onChange={(e) => setIntervalValue(e.target.value)}
            />

            <div className="sm:col-span-2">
              <Input
                label="Точка отправления"
                value={origin}
                placeholder="Например, склад"
                onChange={(e) => setOrigin(e.target.value)}
              />
            </div>
          </div>

          <Button onClick={handleCalculate} isLoading={isLoading}>
            Рассчитать
          </Button>

          {error && <div className="text-sm text-error-600">{error}</div>}
          {resultError && (
            <div className="text-sm text-warning-700 bg-warning-50 rounded-md p-3">
              {resultError}
            </div>
          )}

          {previewData && (
            <div className="rounded border border-gray-200 p-4 space-y-3 text-sm">
              <div className="font-medium text-gray-900">
                {previewData.name}{' '}
                <span className="text-gray-500">
                  ({KIND_LABELS[previewData.kind] ?? '—'})
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">База</span>
                <span>{previewData.base} ₽</span>
              </div>
              {previewData.applied.map((item, index) => (
                <div key={index} className="flex justify-between">
                  <span className="text-gray-600">{item.label}</span>
                  <span>+ {Number(item.amount)} ₽</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-gray-200 pt-2 font-medium text-gray-900">
                <span>Итого</span>
                <span>{previewData.total} ₽</span>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Закрыть
          </Button>
        </div>
      </div>
    </div>
  )
}
